import CheckBoxOutlinedIcon from "@mui/icons-material/CheckBoxOutlined";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import DocumentEditor from "@txtextcontrol/tx-react-document-editor";
import dayjs from "dayjs";
import React, { useContext, useEffect, useRef, useState } from "react";
import accountService from "../api/services/account-service";
import documentService from "../api/services/document-service";
import jobService from "../api/services/job-service";
import textcontrolHeader from "../api/textcontrol-header";
import { showErrorToast, showSuccessToast } from "../helpers/utils";
import UpdateCOTranscriptsModel from "../models/update-cotranscripts-model";
import { pca } from "../msalConfig";
import { PermissionsContext } from "../permission-context";
import SharedTable, { TooltipCell } from "../components/shared-table";

function EMREntry() {
  const [jobDetails, setJobDetails] = useState([]);
  const [account, setAccount] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [accessToken, setAccessToken] = useState("");
  const [selectedRow, setSelectedRow] = useState(null);
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const { loading, error, hasPermission } = useContext(PermissionsContext);
  const isListenerAdded = useRef(false);
  const user = pca && pca.getActiveAccount() ? pca.getActiveAccount().username : "";

  const handleLoaded = async () => {
    if (isLoading) return;
    try {
      setIsLoading(true);
      var jobDetails = {
        JobIds: [String(selectedRow.jobId)],
        AccountId: selectedRow.accountId,
        UserId: user,
        JobAction: "Open",
        ConnectionId: window.TXTextControl.connectionID,
      };
      var res = await documentService.loadDocument(jobDetails);
      if (res.status === 200 || res.status === 201) {
      } else {
        showErrorToast("Failed to load the file.");
      }
    } catch {
      showErrorToast(
        "Exception occured while trying to load the file. Please check your connection or try again later."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleDocumentLoaded = () => {
    window.TXTextControl.editMode = 1;
    window.TXTextControl.clipboardMode = 1;
  };

  const handleDocumentEditorReady = () => {
    if (!isListenerAdded.current) {
      window.TXTextControl.clipboardMode = 1;
      window.TXTextControl.addEventListener("textControlLoaded", handleLoaded);
      window.TXTextControl.addEventListener("documentLoaded", handleDocumentLoaded);
      isListenerAdded.current = true;
    }
  };

  const handleRowClick = async (params) => {
    if (isLoadingData) {
      return;
    }

    try {
      setIsLoadingData(true);
      const res = await textcontrolHeader.getAccessToken();
      setAccessToken(res.data);
      setSelectedRow(params.row);
      setOpen(true);
    } catch (error) {
      showErrorToast("Failed to load row data. Please try again.");
    } finally {
      setIsLoadingData(false);
    }
  };

  const handleClose = () => {
    if (isListenerAdded.current) {
      window.TXTextControl.removeEventListener("textControlLoaded", handleLoaded);
      window.TXTextControl.removeEventListener("documentLoaded", handleDocumentLoaded);
      isListenerAdded.current = false;
      window.TXTextControl.removeFromDom();
    }
    setOpen(false);
  };

  const updateTable = (vfId) => {
    setJobDetails((prevJobDetails) => prevJobDetails.filter((job) => job.vfId !== vfId));
  };

  const handleApprove = async () => {
    try {
      if (isListenerAdded.current) {
        window.TXTextControl.removeEventListener("textControlLoaded", handleLoaded);
        window.TXTextControl.removeEventListener("documentLoaded", handleDocumentLoaded);
        isListenerAdded.current = false;
        window.TXTextControl.removeFromDom();
      }
      var uploadJob = new UpdateCOTranscriptsModel(
        selectedRow.vfId,
        selectedRow.jobId,
        account,
        "U",
        "Manually Uploaded",
        user
      );
      const res = await jobService.updateJob(uploadJob);
      if (res.status === 200 || res.status === 201) {
        showSuccessToast("Mark the job as uploaded");
        updateTable(selectedRow.vfId);
        setOpen(false);
      } else {
        showErrorToast("Failed to mark the job as uploaded. Please try again.");
      }
    } catch {
      showErrorToast("An unexpected error occurred. Please try again later.");
    }
  };

  const handleChange = async (event) => {
    try {
      if (event.target.value !== "No Accounts available") {
        setAccount(event.target.value);
        const res = await jobService.getJobsForAccount(event.target.value);
        if (res.status === 200 || res.status === 201) {
          setJobDetails([...res.data]);
        } else {
          showErrorToast("Failed to load job listings. Please try again later.");
        }
      } else {
        setAccount(event.target.value);
      }
    } catch {
      showErrorToast("An unexpected error occurred. Please try again later.");
    }
  };

  const fetchData = async () => {
    try {
      const res = await accountService.getAccountsForEMRAsync();
      if (res.status === 200 || res.status === 201) {
        setAccounts(res.data || ["No Accounts available"]);
      } else {
        showErrorToast(
          "Unable to fetch accounts. Please check your connection or try again later."
        );
        setAccounts([]);
      }
    } catch (error) {
      showErrorToast(
        "An unexpected error occurred while loading user accounts. Please try again later."
      );
      setAccounts([]);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const columns = [
    {
      dataIndex: "providerName",
      title: "Provider Name",
      width: 150,
      fixed: "left",
      sorter: (a, b) => a.providerName.localeCompare(b.providerName),
      render: (text) => <TooltipCell text={text} width='150px' />,
    },
    {
      dataIndex: "firstName",
      title: "First Name",
      width: 130,
      fixed: "left",
      sorter: (a, b) => a.firstName.localeCompare(b.firstName),
      render: (text) => <TooltipCell text={text} width='130px' />,
    },
    {
      dataIndex: "lastName",
      title: "Last Name",
      width: 120,
      fixed: "left",
      sorter: (a, b) => a.lastName.localeCompare(b.lastName),
      render: (text) => <TooltipCell text={text} width='120px' />,
    },
    {
      dataIndex: "patDOB",
      title: "Pat DOB",
      width: 130,
      fixed: "left",
      render: (text) => (text ? dayjs(text).format("MM-DD-YYYY") : ""),
    },
    {
      dataIndex: "mrn",
      title: "MRN",
      width: 90,
      fixed: "left",
      render: (text) => <TooltipCell text={text} width='90px' />,
    },
    {
      dataIndex: "visitDate",
      title: "Visit Date",
      width: 130,
      fixed: "left",
      sorter: (a, b) => {
        return Date.parse(a.visitDate) - Date.parse(b.visitDate);
      },
      render: (text) => dayjs(text).format("MM-DD-YYYY"),
    },
    {
      dataIndex: "reportType",
      title: "Report Type",
      width: 110,
      render: (text) => text?.toUpperCase(),
    },
    {
      dataIndex: "locationName",
      title: "Location Name",
      width: 140,
      render: (text) => <TooltipCell text={text} width='140px' />,
    },
    {
      dataIndex: "docFileName",
      title: "File Name",
      width: 120,
      render: (text) => <TooltipCell text={text} width='120px' />,
    },
    {
      dataIndex: "pr",
      title: "PR",
      width: 50,
    },
    {
      dataIndex: "approved",
      title: "Approved",
      width: 100,
      render: (approved) => <span>{approved === "Y" ? "TRUE" : "FALSE"}</span>,
    },
    {
      dataIndex: "printDownloadStatus",
      title: "Print Download Status",
      width: 150,
      render: (printDownloadStatus) => (
        <span>{printDownloadStatus === "Y" ? "TRUE" : "FALSE"}</span>
      ),
    },
    {
      dataIndex: "uploaded",
      title: "Uploaded",
      width: 100,
      render: (uploaded) => <span>{uploaded === "true" ? "TRUE" : "FALSE"}</span>,
    },
    {
      dataIndex: "clientNotes",
      title: "Client Notes",
      width: "125px",
      render: (text) => <TooltipCell text={text} width='125px' />,
    },
    {
      dataIndex: "notesToClient",
      title: "Notes To Client",
      width: "125px",
      render: (text) => <TooltipCell text={text} width='125px' />,
    },
  ];

  const rows = jobDetails.map((row, index) => ({
    id: index + 1,
    pr: row.pr,
    vfId: row.vfId,
    jobId: row.jobId,
    mrn: row.mrn,
    accountId: row.accountId,
    visitDate: row.visitDate ? row.visitDate.slice(0, 10) : null,
    reportType: row.reportType,
    approved: row.approved,
    printDownloadStatus: row.printDownloadStatus,
    clientNotes: row.clientNotes,
    uploaded: row.uploaded,
    notesToClient: row.notesToClient,
    patDOB: row.patDOB ? row.patDOB.slice(0, 10) : "",
    lastName: row.lastName,
    firstName: row.firstName,
    locationName: row.locationName,
    providerName: row.providerName,
    docFileName: row.docFileName,
  }));

  return (
    <>
      {!loading && !error && hasPermission("EMR") && (
        <>
          <Box style={{ marginRight: "1%", marginLeft: "2%" }}>
            <Grid
              container
              justifyContent='space-between'
              alignItems='center'
              sx={{
                borderBottom: 1,
                borderColor: "#dfdfdf",
                marginTop: 1.5,
              }}>
              <Grid>
                <Typography
                  gutterBottom
                  fontWeight='700'
                  lineHeight={"31.25px"}
                  fontSize={"24px"}
                  fontStyle={"DM Sans"}>
                  EMR Entry
                </Typography>
              </Grid>
              <Grid>
                <FormControl sx={{ m: 1, minWidth: 120, marginLeft: 2 }} size='small'>
                  <Select
                    labelId='account'
                    id='account'
                    name='account'
                    value={account}
                    sx={{ borderRadius: "12px", height: "24px" }}
                    onChange={handleChange}
                    displayEmpty
                    renderValue={
                      account !== ""
                        ? undefined
                        : () => (
                            <div
                              style={{
                                color: "#777777",
                                fontFamily: "DM Sans",
                                fontWeight: 400,
                                size: "12px",
                              }}>
                              Select Account
                            </div>
                          )
                    }
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 200,
                          padding: "4px 12px",
                        },
                      },
                    }}>
                    {accounts?.map((accountValue, index) => (
                      <MenuItem
                        key={index}
                        value={accountValue}
                        sx={{
                          fontSize: "12px",
                          fontFamily: "DM Sans",
                          lineHeight: "15.62px",
                          color: "#777777",
                          fontWeight: "400",
                        }}>
                        {accountValue}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <div
              style={{
                margin: "12px 0",
                fontFamily: "DM Sans",
                minHeight: "60vh",
              }}>
              <SharedTable
                columns={columns}
                dataSource={rows}
                scroll={{ x: "max-content" }}
                rowKey='id'
                tableName='emrEntry'
                onRow={(record) => ({
                  onClick: () => handleRowClick({ row: record }),
                })}
              />
            </div>
            <Modal
              open={open}
              onClose={handleClose}
              aria-labelledby='modal-box'
              aria-describedby='modal-box-description'>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "flex-start",
                  width: "90%",
                  height: "90vh",
                  borderRadius: "12px",
                  bgcolor: "white",
                  boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.37)",
                  padding: 2,
                  margin: "auto",
                  mt: 5,
                  position: "relative",
                }}>
                <Grid
                  container
                  spacing={2}
                  sx={{
                    width: "100%",
                    height: "100%",
                    maxHeight: { xs: "100%", md: "100%", lg: "100%", xl: "100%" },
                    overflowY: { xs: "auto", md: "visible" },
                  }}>
                  <Grid size={8.5}>
                    <DocumentEditor
                      webSocketURL={`${process.env.REACT_APP_API_URL_WebSocketURI}?access_token=${accessToken}`}
                      editMode='ReadAndSelect'
                      onLoad={() => handleDocumentEditorReady()}></DocumentEditor>
                  </Grid>
                  <Grid
                    container
                    size={3.5}
                    spacing={{ lg: 2, xl: 7 }}
                    direction='column'
                    sx={{ backgroundColor: "white" }}>
                    <Grid
                      container
                      direction='row'
                      size={12}
                      sx={{
                        justifyContent: "flex-end",
                        alignItems: "baseline",
                        height: "60px",
                      }}>
                      <Grid>
                        <Button
                          variant='contained'
                          size='small'
                          sx={{
                            marginRight: 1,
                            gap: 0.5,
                            textTransform: "none",
                          }}
                          onClick={handleApprove}>
                          <CheckBoxOutlinedIcon sx={{ borderRadius: "12px", strokeWidth: 0.5 }} />{" "}
                          <Typography
                            fontWeight='300'
                            lineHeight={"18.23px"}
                            fontSize={"14px"}
                            fontStyle={"DM Sans"}>
                            Mark as Uploaded
                          </Typography>
                        </Button>
                        <IconButton aria-label='delete' onClick={handleClose}>
                          <CloseIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                    <Grid
                      container
                      spacing={{ lg: 2, xl: 3 }}
                      sx={{
                        "& .MuiInputLabel-root": {
                          fontWeight: 400,
                          fontFamily: "DM Sans",
                          color: "#777777",
                          fontSize: "12px",
                        },
                        "& .MuiInputBase-input": {
                          fontSize: "12px",
                          fontWeight: 400,
                          fontFamily: "DM Sans",
                          color: "#121212",
                        },
                      }}>
                      <Grid
                        container
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            height: "40px",
                            borderRadius: "12px",
                            border: "1px",
                          },
                          width: "100%",
                        }}>
                        <Grid size={{ xs: 12, md: 4 }}>
                          <InputLabel>First Name</InputLabel>
                          <TextField
                            id='firstName'
                            variant='outlined'
                            sx={{ width: "100%", pointerEvents: "none" }}
                            defaultValue={selectedRow?.firstName}
                            slotProps={{ input: { readOnly: true } }}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                          <InputLabel>Middle Name</InputLabel>
                          <TextField
                            id='middleName'
                            variant='outlined'
                            sx={{ width: "100%", pointerEvents: "none" }}
                            defaultValue={selectedRow?.middleInitial}
                            slotProps={{ input: { readOnly: true } }}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                          <InputLabel>Last Name</InputLabel>
                          <TextField
                            id='lastName'
                            variant='outlined'
                            sx={{ width: "100%", pointerEvents: "none" }}
                            defaultValue={selectedRow?.lastName}
                            slotProps={{ input: { readOnly: true } }}
                          />
                        </Grid>
                      </Grid>
                      <Grid
                        container
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            height: "40px",
                            borderRadius: "12px",
                          },
                        }}>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <InputLabel>DOB</InputLabel>
                          <TextField
                            id='dob'
                            sx={{ width: "100%", pointerEvents: "none" }}
                            variant='outlined'
                            defaultValue={selectedRow?.patDOB}
                            slotProps={{ input: { readOnly: true } }}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <InputLabel>MRN</InputLabel>
                          <TextField
                            id='mrn'
                            sx={{ width: "100%", pointerEvents: "none" }}
                            variant='outlined'
                            defaultValue={selectedRow?.mrn}
                            slotProps={{ input: { readOnly: true } }}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <InputLabel>Provider Name</InputLabel>
                          <TextField
                            id='providerName'
                            sx={{ width: "100%", pointerEvents: "none" }}
                            variant='outlined'
                            defaultValue={selectedRow?.providerName}
                            slotProps={{ input: { readOnly: true } }}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <InputLabel>Report type</InputLabel>
                          <TextField
                            id='reportType'
                            sx={{ width: "100%", pointerEvents: "none" }}
                            variant='outlined'
                            defaultValue={selectedRow?.reportType}
                            slotProps={{ input: { readOnly: true } }}
                          />
                        </Grid>
                      </Grid>
                      <Grid
                        container
                        size={12}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            width: "100%",
                            height: "84px",
                            borderRadius: "12px",
                          },
                        }}>
                        <Grid size={12}>
                          <InputLabel>Notes to client</InputLabel>
                          <TextField
                            id='notesToClient'
                            multiline
                            rows={3}
                            sx={{ width: "100%", pointerEvents: "none" }}
                            variant='outlined'
                            defaultValue={selectedRow?.notesToClient}
                            slotProps={{ input: { readOnly: true } }}
                          />
                        </Grid>
                        <Grid size={12}>
                          <InputLabel>Client notes</InputLabel>
                          <TextField
                            id='clientNotes'
                            multiline
                            rows={3}
                            sx={{ width: "100%", pointerEvents: "none" }}
                            variant='outlined'
                            defaultValue={selectedRow?.clientNotes}
                            slotProps={{ input: { readOnly: true } }}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Box>
            </Modal>
          </Box>
        </>
      )}
      {!loading && !error && !hasPermission("EMR") && (
        <h1>You Do Not Have Permissions To View Page, Contact Administrator</h1>
      )}
    </>
  );
}

export default EMREntry;
